import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, CalendarCheck, Moon } from "lucide-react";
import { api } from "@/lib/api";
import { fmtOdds, pct, SPORT_META } from "@/lib/odds";
import { useAuth } from "@/context/AuthContext";
import { StreakBadge } from "@/pages/Players";
import { FollowButton } from "@/components/FollowButton";
import { MoveChip } from "@/components/MoveChip";
import { StreakActions } from "@/components/StreakActions";

export function WatchlistPanel() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const slugs = (user?.watchlist || []).join(",");
  const load = useCallback(() => api.get("/watchlist").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, slugs]);

  return (
    <section className="glass-card overflow-hidden" data-testid="watchlist-panel">
      <div className="flex flex-wrap items-center gap-3 border-b border-[#26334D] px-5 py-3">
        <h2 className="font-display flex items-center gap-2 text-xl font-bold uppercase tracking-tight text-white"><Star size={18} className="fill-amber-300 text-amber-300" /> Your Players</h2>
        <p className="text-xs text-slate-400">{d ? `${d.count} followed · streak alerts fire when one of them hits 5 straight` : "Loading…"}</p>
        <Link to="/players" data-testid="watchlist-browse-link" className="ml-auto text-xs font-semibold uppercase tracking-wider text-emerald-400 hover:underline">Browse players →</Link>
      </div>
      {d && d.count === 0 && (
        <p className="px-5 py-5 text-sm text-slate-500" data-testid="watchlist-empty">Follow players from the <Link to="/players" className="text-emerald-400 underline">Players</Link> tab and they'll surface here every morning with their props, the model's calls and any hot streaks.</p>
      )}
      {d && d.count > 0 && (
        <ul className="grid gap-px bg-[#26334D]/40 sm:grid-cols-2 lg:grid-cols-3" data-testid="watchlist-rows">
          {d.players.map((p) => (
            <li key={p.slug} data-testid="watchlist-row" className="flex flex-col gap-2 bg-[#121824] px-4 py-3 transition-colors hover:bg-[#182030]">
              <div className="flex items-center gap-2">
                <span className="label-caps" style={{ color: SPORT_META[p.sport]?.color }}>{SPORT_META[p.sport]?.short}</span>
                <span className="rounded bg-[#0B0E14] px-1.5 py-0.5 font-mono-num text-[10px] text-slate-400">{p.team}</span>
                <span className="ml-auto"><FollowButton slug={p.slug} name={p.name} onChange={() => setTimeout(load, 50)} /></span>
              </div>
              <Link to={`/players/${p.slug}`} data-testid="watchlist-player-link" className="font-display text-xl font-bold leading-none tracking-tight text-white hover:text-emerald-300">{p.name}</Link>
              <p className="flex items-center gap-1.5 text-xs text-slate-400">
                {p.playing_today ? <><CalendarCheck size={12} className="text-emerald-400" /> {p.matchup} · {new Date(p.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {p.props} props</> : <><Moon size={12} /> Off today</>}
              </p>
              {p.calls_today.slice(0, 1).map((c) => (
                <div key={c.pick_id} className="flex items-center gap-2 rounded-md border border-[#26334D] bg-[#0B0E14]/60 px-2 py-1.5 text-xs" data-testid="watchlist-call">
                  <span className="min-w-0 flex-1 truncate text-slate-200">Model: <span className="font-semibold text-white">{c.selection.replace(/^.*?\(\w+\) /, "")}</span></span>
                  <MoveChip movement={c.movement} compact />
                  <span className="font-mono-num text-amber-400">{fmtOdds(c.odds)}</span>
                  <span className="font-mono-num text-emerald-400">{pct(c.win_probability)}</span>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2">
                {p.record.n > 0 && <span className="font-mono-num text-xs text-slate-400" data-testid="watchlist-record"><span className={`font-bold ${p.record.hit_rate >= 55 ? "text-emerald-400" : p.record.hit_rate < 45 ? "text-red-400" : "text-amber-400"}`}>{p.record.hit_rate}%</span> · {p.record.won}–{p.record.lost}</span>}
                <StreakBadge record={p.record} />
                <span className="ml-auto"><StreakActions slug={p.slug} record={p.record} playingToday={p.playing_today} /></span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
